/**
 * Shape of the lawyer handoff context returned by
 * GET /api/v0/thread/{threadId}/lawyer-handoff-context (lazy-load path)
 * or embedded inline on a button payload (document-generation path).
 *
 * When `lawyer_handoff_context` on a button is `null`, it is a lazy-load
 * sentinel — the FE must fetch via the endpoint on click. It is NOT the same
 * as the property being absent entirely.
 * Spec: openspec/changes/lawyer-handoff-context-lazy-load/specs/lazy-handoff-context-fetch/spec.md
 */
import { ConsultationOffer } from "src/domain/entitlements";

export type LawyerHandoffContext = {
  tasks: Array<{ title: string; body: string }>;
  recommendation_level: "low" | "recommended" | "strongly_recommended" | null;
  subheader: string | null;
};

/**
 * Presence-only check for the BE-driven consultation suppression flag.
 * Spec: openspec/specs/legal-review-button-variants/spec.md — the value is opaque;
 * callers MUST NOT inspect, render, log, or branch on the string itself.
 *
 * BE may emit the field as a non-null string when consultation is suppressed,
 * as explicit `null` when not suppressed, or omit it entirely (older threads,
 * /try, anonymous paths). All three of the "not suppressed" variants are
 * treated the same — loose `!= null` check so missing-field is a safe no-op.
 */
export type ConsultationEligibleOffers = {
  lawyer_consultation?: string | null;
  consultation_suppression_reason?: string | null;
  legal_review_title?: string | null;
} | null | undefined;

export const isConsultationSuppressed = (eligibleOffers: ConsultationEligibleOffers): boolean =>
  ConsultationOffer.from(eligibleOffers).isSuppressed();

/**
 * Returns true when the recommendation level warrants showing the "Recommended"
 * tag on the button or badge in the shelf. V3 wire values only — no backward
 * compat (lockstep deploy).
 * Spec: openspec/changes/v3-legal-review-recommendation/specs/legal-review-button-variants/spec.md
 */
export const isConsultationRecommended = (level: string | null | undefined): boolean => {
  if (!level) return false;
  if (level === "recommended" || level === "strongly_recommended") return true;
  // "low" and any unrecognised string
  return false;
};

/**
 * A single entry of the `quick_action_buttons` / `payload.buttons` array.
 *
 * V3.5 (2026-08-17) emits exactly 4 suggestion pills per turn: 1 legal pill
 * (`type: "legal_review_request"`, `is_legal: true`) + 3 next-step pills
 * (`type: "draft"`, `is_legal: false`). V3.0 threads are unchanged — variable
 * length, populated `subtitle`, `is_legal` absent or `false` on everything.
 */
export type QuickActionButton = {
  type?: string;
  text?: string | null;
  subtitle?: string | null;
  is_legal?: boolean;
  hide_from_user?: boolean;
  referral_category?: string | null;
  eligible_offers?: ConsultationEligibleOffers;
  lawyer_handoff_context?: LawyerHandoffContext | null;
  [key: string]: unknown;
};

/**
 * Identifies the V3.5 legal suggestion pill.
 *
 * `type` alone is NOT sufficient: a single turn also carries the always-on
 * "Consult Attorney" button and a hidden doc-generated one, both of which are
 * `type: "legal_review_request"` with `is_legal: false`. Match on the flag.
 */
export const isLegalPill = (btn: QuickActionButton | null | undefined): boolean =>
  btn?.is_legal === true;

/**
 * Finds the button in a turn that carries the consultation offer.
 *
 * The V3.5 legal pill ships `eligible_offers: null`, which is indistinguishable
 * from the out-of-scope sentinel downstream (`tierActions.isOutOfScope` treats a
 * falsy offers object as out-of-scope, so an un-hydrated pill would resolve to
 * `not_available` on the in-shelf submit). The always-on "Consult Attorney"
 * button in the same array carries the turn's real offer, so the pill borrows it.
 *
 * Visible carriers win over `hide_from_user` ones (the hidden doc-generated
 * button is a data carrier of last resort). Returns null when nothing carries an
 * offer — the pill still renders in that case; only the hydration is skipped.
 *
 * NOTE: this also backs the carrier selection in `helpers.findeligibleOffers`,
 * which cannot be covered by its own test — `src/pages/helpers.js` resolves to
 * undefined named exports under `react-scripts test` (same pre-existing failure
 * as the `handleAuthActionType` suites). The selection semantics are tested here
 * instead.
 */
export const findConsultationOfferCarrier = (
  buttons: QuickActionButton[] | null | undefined,
): QuickActionButton | null => {
  const carriers = (buttons || []).filter(
    (btn) => btn?.type === "legal_review_request" && !isLegalPill(btn) && btn?.eligible_offers != null,
  );
  return carriers.find((btn) => !btn.hide_from_user) || carriers[0] || null;
};

/**
 * Thread-wide fallback for `findConsultationOfferCarrier`.
 *
 * Observed on 2026-08-17: a V3.5 turn can arrive as a quick_actions message
 * containing ONLY the 4 pills — the always-on "Consult Attorney" button is
 * emitted on a separate message in the same turn, or not at all. So the carrier
 * is searched across the thread (most recent first), not just the current
 * message's button array.
 */
export const findThreadConsultationOfferCarrier = (
  messages: Array<{ message_type?: string; payload?: { buttons?: QuickActionButton[] | null } | null }> | null | undefined,
): QuickActionButton | null => {
  const list = messages || [];
  for (let i = list.length - 1; i >= 0; i--) {
    const msg = list[i];
    if (msg?.message_type !== "quick_actions" && msg?.message_type !== "quick_action_button_messages") continue;
    const carrier = findConsultationOfferCarrier(msg?.payload?.buttons);
    if (carrier) return carrier;
  }
  return null;
};

type ThreadMessage = {
  message_type: string;
  payload?: {
    doc_id?: string | null;
    buttons?: Array<{
      type?: string;
      referral_category?: string | null;
    }> | null;
  } | null;
};

/**
 * Derives the set of doc_ids for which a doc-level consultation has been
 * confirmed in this thread. Scans for legal_review_message where payload.doc_id
 * is present; these are the server-emitted confirmation signals.
 * Spec: openspec/changes/doc-consultation-requested-via-message/specs/doc-consultation-requested-state/spec.md
 */
export const getRequestedDocConsultationIds = (messages: ThreadMessage[]): Set<string> => {
  const requested = new Set<string>();
  for (const msg of messages) {
    if (msg.message_type === "legal_review_message" && msg.payload?.doc_id) {
      requested.add(msg.payload.doc_id);
    }
  }
  return requested;
};

/**
 * Derives the set of referral_category values for which a chat-level
 * consultation has already been confirmed in this thread.
 *
 * Algorithm: forward scan for legal_review_message (no doc_id), then
 * reverse-search to the nearest quick_actions button's referral_category.
 * FE-only bridge until the backend stores referral_category on submissions.
 * Spec: openspec/changes/loop-in-counsel-fe/specs/consultation-matter-uniqueness/spec.md
 */
export const getRequestedChatConsultationMatters = (messages: ThreadMessage[]): Set<string> => {
  const requested = new Set<string>();

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    if (msg.message_type !== "legal_review_message" || msg.payload?.doc_id) continue;

    for (let j = i - 1; j >= 0; j--) {
      const prev = messages[j];
      if (prev.message_type === "quick_actions" || prev.message_type === "quick_action_button_messages") {
        const btn = prev.payload?.buttons?.find(
          (b) => b.type === "legal_review_request" && b.referral_category,
        );
        if (btn?.referral_category) {
          requested.add(btn.referral_category);
        }
        break;
      }
    }
  }

  return requested;
};
